import { useEffect, useId, useState } from 'react';
import type { ProjectPage } from '@canvas/shared-types';
import type { fabric } from 'fabric';
import { type ExportFormat } from '@/lib/exportCanvas';
import { exportProjectPages, type ExportScope } from '@/lib/exportPages';
// TODO(subscription): re-enable quota UI when Pro plans ship
// import {
//   canExportNow,
//   fetchExportQuota,
//   recordExportSuccess,
//   type ExportQuota,
// } from '@/lib/exportLimits';
import { recordExportSuccess, SUBSCRIPTIONS_ENABLED } from '@/lib/exportLimits';

interface ExportDialogProps {
  open: boolean;
  canvas: fabric.Canvas | null;
  projectName: string;
  pages?: ProjectPage[];
  activePageId?: string;
  /** Flush live canvas into pages before export */
  onPrepareExport?: () => void;
  onClose: () => void;
}

export default function ExportDialog({
  open,
  canvas,
  projectName,
  pages = [],
  activePageId = '',
  onPrepareExport,
  onClose,
}: ExportDialogProps) {
  const titleId = useId();
  const [format, setFormat] = useState<ExportFormat>('png');
  const [scope, setScope] = useState<ExportScope>('active');
  const [scale, setScale] = useState(1);
  const [quality, setQuality] = useState(0.92);
  const [transparentBackground, setTransparentBackground] = useState(false);
  const isRaster = format === 'png' || format === 'jpg' || format === 'pdf';
  const supportsTransparent = format === 'png' || format === 'svg';
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const pageCount = pages.length || 1;
  const multi = pageCount > 1;

  useEffect(() => {
    if (!open) return;
    setError(null);
    setBusy(false);
    if (!multi) setScope('active');

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, multi]);

  if (!open) return null;

  const exportAll = scope === 'all' && multi;
  const downloadHint =
    format === 'pdf'
      ? exportAll
        ? `${projectName || 'craftara-export'}.pdf (${pageCount} pages)`
        : `${projectName || 'craftara-export'}.pdf`
      : exportAll
        ? `${projectName || 'craftara-export'}-pages.zip (${pageCount} × .${format})`
        : `${projectName || 'craftara-export'}.${format}`;

  const handleExport = () => {
    if (!canvas && !pages.length) {
      setError('Canvas is not ready yet.');
      return;
    }

    // Free while testing — re-enable when SUBSCRIPTIONS_ENABLED is true:
    // if (quota) {
    //   const check = canExportNow(quota);
    //   if (!check.ok) {
    //     setError(check.reason || 'Export limit reached');
    //     return;
    //   }
    // }

    setBusy(true);
    setError(null);
    try {
      onPrepareExport?.();
      const pageList =
        pages.length > 0
          ? pages
          : [
              {
                pageId: 'page-1',
                name: 'Page 1',
                canvasData: {
                  version: '5.3.0',
                  objects: [],
                  background: '#ffffff',
                  width: canvas?.getWidth() || 1200,
                  height: canvas?.getHeight() || 800,
                },
              },
            ];

      void exportProjectPages({
        pages: pageList,
        activePageId: activePageId || pageList[0].pageId,
        liveCanvas: canvas,
        format,
        scale: isRaster ? scale : 1,
        quality: format === 'jpg' || format === 'pdf' ? quality : 1,
        projectName,
        scope: exportAll ? 'all' : 'active',
        transparentBackground: supportsTransparent && transparentBackground,
      })
        .then(() => recordExportSuccess())
        .then(() => {
          onClose();
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : 'Export failed');
        })
        .finally(() => setBusy(false));
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error ? err.message : 'Export failed');
    }
  };

  return (
    <div className="ps-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="ps-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ps-modal-header">
          <h2 id={titleId}>Export</h2>
          <button type="button" className="ps-menu-link" onClick={onClose}>
            Close
          </button>
        </header>

        <div className="ps-modal-body">
          <label className="ps-field">
            Format
            <select
              value={format}
              onChange={(e) => {
                const next = e.target.value as ExportFormat;
                setFormat(next);
                if (next === 'jpg' || next === 'pdf') {
                  setTransparentBackground(false);
                }
              }}
            >
              <option value="png">PNG (transparent-friendly)</option>
              <option value="jpg">JPG</option>
              <option value="svg">SVG (vector)</option>
              <option value="pdf">PDF (print / share)</option>
            </select>
          </label>

          <label className="ps-field ps-field-checkbox">
            <span className="ps-checkbox-row">
              <input
                type="checkbox"
                checked={supportsTransparent && transparentBackground}
                disabled={!supportsTransparent}
                onChange={(e) => setTransparentBackground(e.target.checked)}
              />
              Transparent background
            </span>
          </label>
          {supportsTransparent ? (
            transparentBackground ? (
              <p className="ps-hint">
                Artboard fill is omitted. Object colors and image opacity are unchanged.
              </p>
            ) : (
              <p className="ps-hint">
                Leave unchecked to keep the artboard color (usually white).
              </p>
            )
          ) : (
            <p className="ps-hint">Use PNG or SVG for a transparent background.</p>
          )}

          {multi && (
            <label className="ps-field">
              Pages
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value as ExportScope)}
              >
                <option value="active">Current page only</option>
                <option value="all">
                  {format === 'pdf'
                    ? `All pages (${pageCount}) in one PDF`
                    : `All pages (${pageCount}) as ZIP`}
                </option>
              </select>
            </label>
          )}

          {isRaster && (
            <label className="ps-field">
              Scale
              <select
                value={scale}
                onChange={(e) => setScale(Number(e.target.value))}
              >
                <option value={1}>1× — screen</option>
                <option value={2}>2× — retina</option>
                <option value={3}>3× — print-ish</option>
              </select>
            </label>
          )}

          {(format === 'jpg' || format === 'pdf') && (
            <label className="ps-field">
              Quality
              <input
                type="range"
                min={0.5}
                max={1}
                step={0.01}
                value={quality}
                onChange={(e) => setQuality(Number(e.target.value))}
              />
              <span>{Math.round(quality * 100)}%</span>
            </label>
          )}

          {format === 'svg' && (
            <p className="ps-hint">
              Vector export. Complex filters / some image effects may rasterize inside the
              SVG.
            </p>
          )}

          {format === 'pdf' && (
            <p className="ps-hint">
              Pages are embedded as JPEG images (white background). Multi-page projects
              become one PDF.
            </p>
          )}

          <p className="ps-hint">
            Downloads <strong>{downloadHint}</strong>
          </p>
          {/* TODO(subscription): restore quota / upgrade UI when billing ships
          <p className="ps-hint">Counts as 1 export toward your monthly quota.</p>
          <p className={`ps-hint ${gate.ok ? '' : 'ps-error'}`}>{quotaLabel(quota)}</p>
          {!gate.ok && (
            <div className="ps-upgrade-card">...</div>
          )}
          */}
          {!SUBSCRIPTIONS_ENABLED && (
            <p className="ps-hint">Exports are unlimited while Craftara is in free preview.</p>
          )}

          {error && <p className="ps-error">{error}</p>}
        </div>

        <footer className="ps-modal-footer">
          <button type="button" className="ps-btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ps-btn primary"
            disabled={busy}
            onClick={handleExport}
          >
            {busy
              ? 'Exporting…'
              : exportAll && format !== 'pdf'
                ? 'Download ZIP'
                : 'Download'}
          </button>
        </footer>
      </div>
    </div>
  );
}
